import { useState, useEffect, useCallback } from 'react';
import { checkinsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  QrCode, UserCheck, Clock, TrendingUp, Users, Search, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer
} from 'recharts';
import useMitgliederSuche, { trefferZusatz } from '@/hooks/useMitgliederSuche';

export default function CheckInPage({ eingebettet = false }) {
  const { user } = useAuth();
  const [todayCheckins, setTodayCheckins] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [qrDialog, setQrDialog] = useState(null);
  const [studioQr, setStudioQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const fetchData = useCallback(async () => {
    try {
      const [todayRes, statsRes] = await Promise.all([
        checkinsAPI.getToday(),
        checkinsAPI.getStats(),
      ]);
      setTodayCheckins(todayRes.data.checkins);
      setStats(statsRes.data);
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await checkinsAPI.getToday();
        setTodayCheckins(res.data.checkins);
      } catch (e) {  }
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleCheckin = async (memberId, memberName) => {
    try {
      await checkinsAPI.checkin({ member_id: memberId, method: 'manual' });
      toast.success(`${memberName} eingecheckt!`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Check-in fehlgeschlagen');
    }
  };

  const handleShowQR = async (memberId) => {
    try {
      const res = await checkinsAPI.getQRCode(memberId);
      setQrDialog(res.data);
    } catch (err) {
      toast.error('QR-Code konnte nicht generiert werden');
    }
  };

  const handleShowStudioQR = async () => {
    try {
      const res = await checkinsAPI.getStudioQRCode();
      setStudioQr(res.data.qr_code);
    } catch (err) {
      toast.error('QR-Code konnte nicht generiert werden');
    }
  };

  // Sucht serverseitig über ALLE Personen — die Seite lädt keine Lead-Liste mehr auf Vorrat
  // (bis 03.10.2026 kamen so nur die 50 neuesten Leads an).
  const suche = useMitgliederSuche(search, { limit: 20 });

  // Nur wer einen OFFENEN Check-in hat, gilt als eingecheckt — nach dem
  // Check-out (Empfang/Liste) verschwindet das Badge und erneutes Einchecken geht.
  const checkedInIds = new Set(todayCheckins.filter(c => !c.checked_out_at).map(c => c.member_id));

  if (loading) {
    return (
      <div className="space-y-6 fade-in">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={`skeleton-${i}`} className="h-28 bg-muted animate-pulse rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="checkin-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          {eingebettet
            ? <h2 className="text-lg font-semibold">Statistik & Studio-QR</h2>
            : <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Check-in</h1>}
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
            {eingebettet ? 'Check-ins heute, diese Woche, Wochenüberblick — Einchecken selbst läuft über den Empfang.' : 'Mitglieder einchecken und Statistiken einsehen'}
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" variant="outline" className="gap-1.5 flex-shrink-0" onClick={handleShowStudioQR} data-testid="show-studio-qr-btn">
            <QrCode className="w-3.5 h-3.5" /> Studio-QR-Code
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border-border/60">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">Heute</p>
                <p className="text-xl sm:text-3xl font-bold">{stats?.today || 0}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-marke/10 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-marke" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">Diese Woche</p>
                <p className="text-xl sm:text-3xl font-bold">{stats?.week || 0}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#06B6D4]/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#06B6D4]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">Dieser Monat</p>
                <p className="text-xl sm:text-3xl font-bold">{stats?.month || 0}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#10B981]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">Gesamt</p>
                <p className="text-xl sm:text-3xl font-bold">{stats?.total || 0}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#8B5CF6]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Member Check-in */}
        <Card className="lg:col-span-5 border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Mitglied einchecken</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="checkin-search"
                placeholder="Name oder Mitgliedsnummer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {suche.treffer.map((m) => {
                const isCheckedIn = checkedInIds.has(m.id);
                const zusatz = trefferZusatz(m);
                return (
                  <div key={m.id} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/50 group" data-testid={`member-row-${m.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${isCheckedIn ? 'bg-[#10B981]' : 'bg-muted-foreground/30'}`}>
                        {m.name?.[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {m.name}
                          {m.archiviert && <span className="ml-1.5 align-middle text-[9px] font-semibold uppercase tracking-wide text-muted-foreground border border-border rounded px-1 py-0.5">Archiv</span>}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">{zusatz}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isCheckedIn ? (
                        <Badge variant="outline" className="text-[10px] border-[#10B981] text-[#10B981]">Eingecheckt</Badge>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleShowQR(m.id)} data-testid={`qr-btn-${m.id}`}>
                            <QrCode className="w-3 h-3" /> QR
                          </Button>
                          <Button size="sm" className="h-7 text-xs bg-marke hover:bg-marke-dunkel" onClick={() => handleCheckin(m.id, m.name)} data-testid={`checkin-btn-${m.id}`}>
                            <UserCheck className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {suche.mehr && (
                <p className="text-xs text-muted-foreground text-center py-2">Es gibt weitere Treffer – bitte genauer suchen.</p>
              )}
              {suche.leer ? (
                <p className="text-sm text-muted-foreground text-center py-6">Namen oder Mitgliedsnummer eingeben.</p>
              ) : suche.zuKurz ? (
                <p className="text-sm text-muted-foreground text-center py-6">Mindestens 2 Zeichen eingeben.</p>
              ) : suche.laedt ? (
                <p className="text-sm text-muted-foreground text-center py-6">Sucht…</p>
              ) : suche.fehler ? (
                <div className="text-center py-5 space-y-2" data-testid="checkin-search-fehler">
                  {suche.keinRecht ? (
                    <p className="text-sm text-red-600 dark:text-red-400">Dieses Konto darf am Empfang nicht suchen. Bitte die Leitung ansprechen.</p>
                  ) : (
                    <>
                      <p className="text-sm text-red-600 dark:text-red-400">Die Suche hat nicht geantwortet. Es wurde nicht gesucht.</p>
                      <Button size="sm" variant="outline" onClick={suche.erneut}>Erneut suchen</Button>
                    </>
                  )}
                </div>
              ) : suche.treffer.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">Keine Mitglieder gefunden</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Today's Check-ins */}
        <Card className="lg:col-span-4 border-border/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Heutige Check-ins</CardTitle>
              <Badge variant="secondary" className="text-[10px]">{todayCheckins.length} gesamt</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {todayCheckins.map((ci) => {
                const anwesend = !ci.checked_out_at;
                const methodLabel = { qr: 'QR-Code', app: 'App', empfang: 'Empfang' }[ci.method] || 'Manuell';
                return (
                  <div key={ci.id} className="flex items-center justify-between py-2 px-2 rounded-lg bg-muted/30 gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${anwesend ? 'bg-[#10B981]/10' : 'bg-muted'}`}>
                        <UserCheck className={`w-3.5 h-3.5 ${anwesend ? 'text-[#10B981]' : 'text-muted-foreground/50'}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{ci.member_name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {methodLabel} · {new Date(ci.checked_in_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          {!anwesend && ` – ${new Date(ci.checked_out_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`}
                        </p>
                      </div>
                    </div>
                    {anwesend ? (
                      <button
                        onClick={async () => { try { await checkinsAPI.checkout(ci.member_id); toast.success(`${ci.member_name} ausgecheckt`); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || 'Check-out fehlgeschlagen'); } }}
                        className="text-[10px] font-semibold text-red-500 border border-red-200 dark:border-red-900/40 rounded-md px-2 py-1 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
                        data-testid={`checkout-${ci.id}`}
                      >
                        Check-out
                      </button>
                    ) : (
                      <Badge variant="secondary" className="text-[9px] flex-shrink-0">Ausgecheckt</Badge>
                    )}
                  </div>
                );
              })}
              {todayCheckins.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Noch keine Check-ins heute</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Weekly Chart */}
        <Card className="lg:col-span-3 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Wochenüberblick</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.daily && (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                  <RechartsTooltip
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(v) => [v, 'Check-ins']}
                  />
                  <Bar dataKey="count" fill="#E60000" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={!!qrDialog} onOpenChange={() => setQrDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>QR-Code für {qrDialog?.member_name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            {qrDialog?.qr_code && (
              <img src={qrDialog.qr_code} alt="QR Code" className="w-64 h-64" data-testid="qr-code-image" />
            )}
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Diesen QR-Code scannen, um {qrDialog?.member_name} einzuchecken
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Studio QR Code Dialog */}
      <Dialog open={!!studioQr} onOpenChange={() => setStudioQr(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Universeller Studio-QR-Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            {studioQr && (
              <img src={studioQr} alt="Studio QR Code" className="w-64 h-64" data-testid="studio-qr-code-image" />
            )}
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Einmal ausdrucken und dauerhaft am Eingang aushängen. Mitglieder scannen ihn mit der App —
              das CRM checkt sie ein und gleicht automatisch ab, ob sie für einen der heutigen Kurse
              eingetragen sind.
            </p>
            <Button onClick={() => window.print()} className="mt-4 gap-1.5" size="sm">
              Drucken
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
